import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const StrategicAlignmentPanel = ({ partnerData, onUpdate, enablementScore }) => {
  const [selectedTier, setSelectedTier] = useState(partnerData?.strategicTier || 'gold');
  const [industryFocus, setIndustryFocus] = useState(partnerData?.industryFocus || []);
  const [roadmapFit, setRoadmapFit] = useState(partnerData?.roadmapFit || {
    technology: 75,
    market: 80,
    innovation: 65,
    sustainability: 70
  });

  const tierOptions = [
    { value: 'gold', label: 'Gold Partner', description: 'Top-tier strategic partnership' },
    { value: 'silver', label: 'Silver Partner', description: 'High-value partnership' },
    { value: 'strategic', label: 'Strategic Partner', description: 'Long-term strategic alignment' },
    { value: 'tactical', label: 'Tactical Partner', description: 'Project-based collaboration' }
  ];

  const industries = [
    { id: 'telecom', label: 'Telecommunications', icon: 'Radio' },
    { id: 'finance', label: 'Financial Services', icon: 'DollarSign' },
    { id: 'healthcare', label: 'Healthcare', icon: 'Heart' },
    { id: 'manufacturing', label: 'Manufacturing', icon: 'Factory' },
    { id: 'retail', label: 'Retail & E-commerce', icon: 'ShoppingCart' },
    { id: 'government', label: 'Government & Public Sector', icon: 'Building2' },
    { id: 'education', label: 'Education', icon: 'GraduationCap' },
    { id: 'energy', label: 'Energy & Utilities', icon: 'Zap' }
  ];

  const getTierColor = (tier) => {
    switch (tier) {
      case 'gold': return 'bg-warning/10 text-warning border-warning/20';
      case 'silver': return 'bg-muted text-muted-foreground border-border';
      case 'strategic': return 'bg-primary/10 text-primary border-primary/20';
      case 'tactical': return 'bg-accent/10 text-accent border-accent/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  // Tier weight for composite score
  const tierWeight = useMemo(() => {
    switch (selectedTier) {
      case 'gold': return 100;
      case 'silver': return 80;
      case 'strategic': return 90;
      case 'tactical': return 60;
      default: return 70;
    }
  }, [selectedTier]);

  // Composite Partnership Health Index
  const healthIndex = useMemo(() => {
    const roadmapAvg = Math.round(Object.values(roadmapFit)?.reduce((a, b) => a + b, 0) / Object.keys(roadmapFit)?.length);
    const industryScore = Math.min(100, (industryFocus?.length / industries?.length) * 100 * 1.5);
    const enablement = enablementScore != null ? enablementScore : 70;
    // Weighted composite: 30% tier, 35% roadmap, 20% industry, 15% enablement
    const composite = Math.round(
      (tierWeight * 0.30) +
      (roadmapAvg * 0.35) +
      (industryScore * 0.20) +
      (enablement * 0.15)
    );
    return Math.min(100, composite);
  }, [tierWeight, roadmapFit, industryFocus, enablementScore]);

  const getHealthLabel = (score) => {
    if (score >= 85) return { label: 'Excellent', color: 'text-success', bg: 'bg-success/10 border-success/20' };
    if (score >= 70) return { label: 'Good', color: 'text-primary', bg: 'bg-primary/10 border-primary/20' };
    if (score >= 55) return { label: 'Fair', color: 'text-warning', bg: 'bg-warning/10 border-warning/20' };
    return { label: 'Needs Improvement', color: 'text-error', bg: 'bg-error/10 border-error/20' };
  };

  const health = getHealthLabel(healthIndex);

  const handleIndustryToggle = (industryId) => {
    const updated = industryFocus?.includes(industryId)
      ? industryFocus?.filter(id => id !== industryId)
      : [...industryFocus, industryId];
    setIndustryFocus(updated);
    onUpdate?.({ industryFocus: updated });
  };

  const handleRoadmapChange = (dimension, value) => {
    const updated = { ...roadmapFit, [dimension]: value };
    setRoadmapFit(updated);
    onUpdate?.({ roadmapFit: updated });
  };

  const handleTierChange = (value) => {
    setSelectedTier(value);
    onUpdate?.({ strategicTier: value });
  };

  return (
    <div className="space-y-24">
      <div>
        <label className="block text-sm font-medium text-foreground mb-12">Strategic Tier Classification</label>
        <Select
          options={tierOptions}
          value={selectedTier}
          onChange={handleTierChange}
          placeholder="Select partnership tier"
        />
        <div className={`mt-12 p-12 rounded-md border ${getTierColor(selectedTier)}`}>
          <div className="flex items-center gap-8">
            <Icon name="Award" size={16} />
            <span className="text-sm font-medium">
              Current Tier: {tierOptions?.find(t => t?.value === selectedTier)?.label || 'Not Set'}
            </span>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-12">Industry Focus Alignment</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {industries?.map((industry) => (
            <div
              key={industry?.id}
              className={`p-12 rounded-md border transition-smooth cursor-pointer ${
                industryFocus?.includes(industry?.id)
                  ? 'bg-primary/10 border-primary' :'bg-muted/50 border-border hover:border-primary/50'
              }`}
              onClick={() => handleIndustryToggle(industry?.id)}
            >
              <Checkbox
                label={
                  <div className="flex items-center gap-8">
                    <Icon name={industry?.icon} size={16} />
                    <span className="text-sm">{industry?.label}</span>
                  </div>
                }
                checked={industryFocus?.includes(industry?.id)}
                onChange={() => handleIndustryToggle(industry?.id)}
              />
            </div>
          ))}
        </div>
        <div className="mt-12 text-xs text-muted-foreground caption">
          {industryFocus?.length} of {industries?.length} industries selected
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-12">Technology Roadmap Fit Assessment</label>
        <div className="space-y-16">
          {Object.entries(roadmapFit)?.map(([dimension, value]) => (
            <div key={dimension}>
              <div className="flex items-center justify-between mb-8">
                <span className="text-sm text-foreground capitalize">{dimension} Alignment</span>
                <span className="text-sm font-semibold text-primary data-text">{value}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={value}
                onChange={(e) => handleRoadmapChange(dimension, parseInt(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${value}%, var(--color-muted) ${value}%, var(--color-muted) 100%)`
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Enhanced Partnership Health Index */}
      <div className={`p-16 rounded-lg border ${health?.bg}`}>
        <div className="flex items-start gap-12">
          <Icon name="Activity" size={18} className={`${health?.color} flex-shrink-0 mt-2`} />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-8">
              <h4 className="text-sm font-semibold text-foreground">Partnership Health Index</h4>
              <div className={`text-2xl font-bold data-text ${health?.color}`}>{healthIndex}</div>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden mb-8">
              <div className={`h-full transition-smooth ${health?.color?.replace('text-', 'bg-')}`} style={{ width: `${healthIndex}%` }} />
            </div>
            <p className="text-xs text-muted-foreground">
              <span className={`font-semibold ${health?.color}`}>{health?.label}</span>
              {' '}— Composite of tier ({tierWeight}), roadmap fit ({Math.round(Object.values(roadmapFit)?.reduce((a, b) => a + b, 0) / 4)}%), industry breadth ({industryFocus?.length}/{industries?.length})
              {enablementScore != null && `, enablement score (${enablementScore})`}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategicAlignmentPanel;